import React, { useState, useEffect } from "react";
import { getPortfolioSectors } from "../lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Loader2, ChevronDown, ChevronRight, TrendingUp, TrendingDown } from "lucide-react";

const COLORS = ["#007AFF", "#10B981", "#F59E0B", "#FF3B30", "#8B5CF6", "#EC4899", "#06B6D4", "#64748B", "#A855F7", "#EF4444", "#14B8A6", "#F97316"];

export default function SectorsPage({ onStockClick }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("global");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    getPortfolioSectors().then(r => setData(r.data));
  }, []);

  const toggle = (sector) => setExpanded(e => ({ ...e, [sector]: !e[sector] }));

  if (!data) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[var(--brand)]" /></div>;

  const sectors = data[view] || [];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6" data-testid="sectors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Sector Allocation</h1>
          <p className="text-sm text-[var(--text-muted)]">Portfolio distribution across sectors</p>
        </div>
        <div className="flex gap-1 bg-[var(--bg-surface)] border border-white/10 rounded-md p-0.5" data-testid="sector-view-toggle">
          {[["global", "Global"], ["india", "India"], ["us", "US"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${view === k ? "bg-[var(--brand)] text-white" : "text-[var(--text-secondary)] hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5" data-testid="sector-pie">
          <div className="text-sm tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-4">Distribution</div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={sectors.slice(0, 10)} cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={2} dataKey="percentage" nameKey="sector" stroke="none">
                {sectors.slice(0, 10).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip content={<STooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar Chart */}
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5" data-testid="sector-bar">
          <div className="text-sm tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-4">By Value</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sectors.slice(0, 10)} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={v => `${(v/100000).toFixed(0)}L`} />
              <YAxis type="category" dataKey="sector" tick={{ fill: "#94A3B8", fontSize: 11 }} width={80} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {sectors.slice(0, 10).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Expandable Sector Table */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden" data-testid="sector-table">
        <div className="p-4 border-b border-white/10">
          <span className="text-sm tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">All Sectors ({sectors.length})</span>
        </div>
        <div className="divide-y divide-white/5">
          {sectors.map((s, i) => {
            const isOpen = expanded[s.sector];
            const stocks = s.stocks || [];
            return (
              <div key={s.sector}>
                {/* Sector Row */}
                <div
                  className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] cursor-pointer transition-colors"
                  onClick={() => toggle(s.sector)}
                  data-testid={`sector-row-${s.sector}`}
                >
                  <div className="flex items-center gap-3">
                    {isOpen
                      ? <ChevronDown size={14} className="text-[var(--text-muted)]" />
                      : <ChevronRight size={14} className="text-[var(--text-muted)]" />}
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-sm text-white">{s.sector}</span>
                    <span className="text-[10px] text-[var(--text-muted)] bg-white/5 px-1.5 py-0.5 rounded">{s.count} {s.count === 1 ? "stock" : "stocks"}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="font-mono text-sm text-[var(--text-secondary)]">{fmtVal(s.value)}</span>
                    <span className="font-mono text-sm text-white w-16 text-right">{s.percentage}%</span>
                    <div className="w-24 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(s.percentage, 100)}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                </div>

                {/* Expanded Stock List */}
                {isOpen && stocks.length > 0 && (
                  <div className="bg-[var(--bg-base)] border-t border-white/5">
                    {/* Stock Header */}
                    <div className="grid grid-cols-12 gap-2 px-5 py-2 text-[9px] uppercase tracking-wider text-[var(--text-muted)] font-semibold border-b border-white/5">
                      <div className="col-span-3">Stock</div>
                      <div className="col-span-1 text-right">Weight</div>
                      <div className="col-span-1 text-right">Qty</div>
                      <div className="col-span-2 text-right">Avg / CMP</div>
                      <div className="col-span-2 text-right">Invested</div>
                      <div className="col-span-1 text-right">Value</div>
                      <div className="col-span-2 text-right">P&L</div>
                    </div>
                    {/* Stock Rows */}
                    {stocks.map(st => (
                      <div
                        key={`${st.symbol}-${st.account}`}
                        className="grid grid-cols-12 gap-2 px-5 py-2.5 hover:bg-white/[0.02] cursor-pointer transition-colors border-b border-white/[0.03] last:border-0"
                        onClick={(e) => { e.stopPropagation(); onStockClick?.(st.symbol); }}
                        data-testid={`sector-stock-${st.symbol}`}
                      >
                        <div className="col-span-3">
                          <div className="font-mono text-xs text-white">{st.symbol}</div>
                          <div className="text-[10px] text-[var(--text-muted)]">{st.account}</div>
                        </div>
                        <div className="col-span-1 text-right font-mono text-xs text-[var(--text-secondary)]">{st.weight}%</div>
                        <div className="col-span-1 text-right font-mono text-xs text-[var(--text-secondary)]">{st.quantity}</div>
                        <div className="col-span-2 text-right">
                          <div className="font-mono text-[10px] text-[var(--text-muted)]">{st.avg_price > 0 ? fmtNum(st.avg_price) : "-"}</div>
                          <div className="font-mono text-xs text-white">{st.last_price > 0 ? fmtNum(st.last_price) : "-"}</div>
                        </div>
                        <div className="col-span-2 text-right font-mono text-xs text-[var(--text-secondary)]">{fmtVal(st.invested)}</div>
                        <div className="col-span-1 text-right font-mono text-xs text-white">{fmtVal(st.value)}</div>
                        <div className="col-span-2 text-right">
                          <div className={`font-mono text-xs flex items-center justify-end gap-1 ${st.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                            {st.pnl >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                            {st.pnl >= 0 ? "+" : ""}{fmtVal(st.pnl)}
                          </div>
                          <div className={`font-mono text-[10px] ${st.pnl_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                            {st.pnl_pct >= 0 ? "+" : ""}{st.pnl_pct?.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Sector Total */}
                    <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-white/[0.02] border-t border-white/5 text-[10px] font-semibold">
                      <div className="col-span-3 text-[var(--text-muted)] uppercase">{stocks.length} stocks total</div>
                      <div className="col-span-1 text-right font-mono text-[var(--text-secondary)]">{s.percentage}%</div>
                      <div className="col-span-1"></div>
                      <div className="col-span-2"></div>
                      <div className="col-span-2 text-right font-mono text-[var(--text-secondary)]">{fmtVal(stocks.reduce((sum, st) => sum + (st.invested || 0), 0))}</div>
                      <div className="col-span-1 text-right font-mono text-white">{fmtVal(s.value)}</div>
                      <div className="col-span-2 text-right">
                        {(() => { const pnl = s.value - stocks.reduce((sum, st) => sum + (st.invested || 0), 0); return (
                          <span className={`font-mono ${pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{pnl >= 0 ? "+" : ""}{fmtVal(pnl)}</span>
                        ); })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function STooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs shadow-xl">
      <div className="text-white font-medium">{d.sector}</div>
      <div className="font-mono text-[var(--text-secondary)]">{d.percentage}% | {fmtVal(d.value)}</div>
      <div className="text-[10px] text-[var(--text-muted)]">{d.count} stocks</div>
    </div>
  );
}

function fmtVal(v) {
  if (!v && v !== 0) return "-";
  const abs = Math.abs(v);
  if (abs >= 10000000) return `₹${(v/10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `₹${(v/100000).toFixed(2)}L`;
  if (abs >= 1000) return `₹${(v/1000).toFixed(1)}K`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function fmtNum(v) {
  if (!v && v !== 0) return "-";
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
